import { useState } from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';
import { SEO } from '../../utils/seo';

export function FlaggenPage() {
  const [selectedCategory, setSelectedCategory] = useState('alle');

  const categories = [
    { id: 'alle', name: 'Alle Flaggen' },
    { id: 'beachflags', name: 'Beachflags' },
    { id: 'fahnenmast', name: 'Fahnenmast-Flaggen' },
    { id: 'standflaggen', name: 'Standflaggen' },
    { id: 'tischflaggen', name: 'Tischflaggen' },
    { id: 'wimpelketten', name: 'Wimpelketten' }
  ];

  const products = [
    {
      id: 1,
      category: 'beachflags',
      name: 'Beachflags',
      description: 'Werbewirksame Strandfahnen für maximale Aufmerksamkeit im Außenbereich. Ideal für Events, Messen und Promotionen.',
      features: ['Wetterfest', 'UV-beständig', 'Verschiedene Größen', 'Stabiler Standfuß'],
      image: 'https://static.readdy.ai/image/07f91bd9f2ad3b84149b39a4e0627797/98415bbdaefaf32662d2aaa05399f391.jpeg',
      sizes: ['2,5m', '3,5m', '4,5m', '5,5m']
    },
    {
      id: 2,
      category: 'fahnenmast',
      name: 'Fahnenmast-Flaggen',
      description: 'Klassische Fahnen für Fahnenmasten - perfekt für Unternehmensgebäude und repräsentative Anlässe.',
      features: ['Hochwertige Materialien', 'Professionelle Konfektionierung', 'Wetterfest', 'Verschiedene Befestigungen'],
      image: 'https://static.readdy.ai/image/07f91bd9f2ad3b84149b39a4e0627797/8c594424e95339a68058c0aa109af6a1.jpeg',
      sizes: ['100 x 150cm', '120 x 200cm', '150 x 250cm']
    },
    {
      id: 3,
      category: 'standflaggen',
      name: 'Standflaggen',
      description: 'Repräsentative Standfahnen für Büros, Konferenzräume und offizielle Anlässe.',
      features: ['Edle Materialien', 'Stabiler Standfuß', 'Höhenverstellbar', 'Professionelles Design'],
      image: 'https://static.readdy.ai/image/07f91bd9f2ad3b84149b39a4e0627797/2d545ffa65c6c67cad9ee7e558200110.png',
      sizes: ['2m', '2,5m', '3m']
    },
    {
      id: 4,
      category: 'tischflaggen',
      name: 'Tischflaggen',
      description: 'Elegante Tischflaggen für Büros, Konferenzräume und repräsentative Anlässe.',
      features: ['Edle Materialien', 'Hochglanz-Standfuß', 'Kompakte Größe', 'Magnetbefestigung'],
      image: 'https://static.readdy.ai/image/07f91bd9f2ad3b84149b39a4e0627797/341875fdcd1be9c1c0a5c67d31c1b98f.jpeg',
      sizes: ['15 x 22cm', '20 x 30cm', '25 x 38cm']
    },
    {
      id: 5,
      category: 'wimpelketten',
      name: 'Wimpelketten',
      description: 'Auffällige Wimpelketten für Events, Eröffnungen und Sonderaktionen.',
      features: ['Wetterfest', 'Individuelle Länge', 'Leuchtende Farben', 'Einfache Montage'],
      image: 'https://static.readdy.ai/image/07f91bd9f2ad3b84149b39a4e0627797/9576c5f6f52ca6347449912e8b739cb7.jpeg',
      sizes: ['50x200cm', '75x300cm', '100x400']
    }
  ];

  const filteredProducts = selectedCategory === 'alle' 
    ? products 
    : products.filter(product => product.category === selectedCategory);

  const schema = {
    '@type': 'CollectionPage',
    name: 'Flaggen & Banner - Professionelle Fahnen für jeden Einsatzbereich',
    description: 'Hochwertige Fahnen und Banner für Messen, Events und dauerhafte Außenwerbung. Von Beachflags bis Wimpelketten.',
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: products.map((product, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Product',
          name: product.name,
          description: product.description
        }
      }))
    }
  };

  return (
    <>
      <SEO
        title="Flaggen & Banner - Professionelle Fahnen für Messen & Events | Horizon Signage"
        description="Hochwertige Fahnen und Banner für jeden Einsatzbereich: Messen, Events, dauerhafte Außenwerbung. Beachflags, Fahnenmast-Flaggen, Standflaggen, Tischflaggen und Wimpelketten. Wetterfest und UV-beständig."
        keywords="Flaggen, Banner, Fahnen, Messen, Events, Außenwerbung, Beachflags, Deutschland"
        canonical={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/flaggen`}
        schema={schema}
      />
      <div className="min-h-screen">
        <Header />
        
        <main className="pt-20">
          {/* Hero Section */}
          <section className="py-20 bg-gradient-to-br from-amber-50 to-white">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              <div className="text-center">
                <span className="inline-block px-4 py-2 bg-zinc-800 text-white rounded-full text-sm font-semibold uppercase tracking-wider mb-6">
                  <i className="ri-flag-line mr-2"></i>
                  Flaggen
                </span>
                <h1 className="text-5xl lg:text-6xl font-bold text-zinc-800 leading-tight mb-6">
                  <span className="block">Professionelle</span>
                  <span className="block text-gray-600">Fahnen & Banner</span>
                </h1>
                <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
                  Entdecken Sie unser umfassendes Sortiment an hochwertigen Fahnen und 
                  Bannern für jeden Einsatzbereich - von Messen über Events bis hin zu 
                  dauerhafter Außenwerbung.
                </p>
              </div>
            </div>
          </section>

          {/* Category Filter */}
          <section className="py-8 bg-white border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              <div className="flex flex-wrap justify-center gap-4">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`px-6 py-3 rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer ${
                      selectedCategory === category.id
                        ? 'bg-zinc-800 text-white shadow-lg'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {category.name}
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* Products Grid */}
          <section className="py-20 bg-gray-50">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredProducts.map((product) => (
                  <div
                    key={product.id}
                    className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                    data-product-shop
                  >
                    {/* Product Image */}
                    <div className="aspect-w-4 aspect-h-5">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-64 object-cover object-center"
                      />
                    </div>

                    {/* Product Content */}
                    <div className="p-8">
                      {/* Category Badge */}
                      <div className="mb-4">
                        <span className="inline-block px-3 py-1 bg-amber-100 text-amber-800 text-xs font-semibold rounded-full uppercase tracking-wider">
                          {categories.find(cat => cat.id === product.category)?.name}
                        </span>
                      </div>

                      {/* Product Name */}
                      <h3 className="text-xl font-bold text-zinc-800 mb-3">
                        {product.name}
                      </h3>

                      {/* Description */}
                      <p className="text-gray-600 mb-4 leading-relaxed">
                        {product.description}
                      </p>

                      {/* Features */}
                      <div className="mb-6">
                        <h4 className="text-sm font-semibold text-zinc-800 mb-2">Eigenschaften:</h4>
                        <ul className="grid grid-cols-2 gap-1 text-sm text-gray-600">
                          {product.features.map((feature, index) => (
                            <li key={index} className="flex items-center">
                              <i className="ri-check-line text-green-500 mr-1 text-xs"></i>
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Sizes */}
                      <div className="mb-6">
                        <h4 className="text-sm font-semibold text-zinc-800 mb-2">Verfügbare Größen:</h4>
                        <div className="flex flex-wrap gap-2">
                          {product.sizes.map((size, index) => (
                            <span key={index} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                              {size}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* CTA Button */}
                      <a
                        href="/kontakt"
                        className="w-full inline-flex items-center justify-center px-6 py-3 border-2 border-zinc-800 text-zinc-800 font-semibold rounded-lg hover:bg-zinc-800 hover:text-white transition-colors whitespace-nowrap cursor-pointer"
                      >
                        Anfrage senden
                        <i className="ri-mail-line ml-2"></i>
                      </a>
                    </div>
                  </div>
                ))}
              </div>

              {/* Bottom CTA */}
              <div className="text-center mt-16">
                <div className="bg-white rounded-2xl p-8 max-w-4xl mx-auto shadow-xl">
                  <h3 className="text-2xl font-bold text-zinc-800 mb-4">
                    Individuelle Flagge nicht gefunden?
                  </h3>
                  <p className="text-gray-600 text-lg mb-6">
                    Wir fertigen auch maßgeschneiderte Lösungen nach Ihren exakten Vorgaben. 
                    Kontaktieren Sie uns für eine persönliche Beratung.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <a
                      href="/kontakt"
                      className="inline-flex items-center justify-center px-8 py-4 bg-zinc-800 text-white font-semibold rounded-lg hover:bg-zinc-900 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Individuelle Anfrage
                      <i className="ri-arrow-right-line ml-2"></i>
                    </a>
                    <a
                      href="tel:+4917296060061"
                      className="inline-flex items-center justify-center px-8 py-4 border-2 border-zinc-800 text-zinc-800 font-semibold rounded-lg hover:bg-zinc-800 hover:text-white transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Telefonberatung
                      <i className="ri-phone-line ml-2"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}
